import type { JSX } from 'react';
import { failureFrom } from '@/lib/result';
import { card, monoLabel } from '../shell/look';
import { PageHead } from '../shell/page-head';
import { EmptyState, ErrorState, LoadingState } from '../shell/states';
import { AlertCard } from './alert-card';
import { HEALTH_COLUMNS, HealthRow } from './health-row';
import { useSourceHealth } from './use-source-health';

/** Diagnostics (design L508–539): what is failing first, then every source's latency. */
export function HealthPage(): JSX.Element {
  const health = useSourceHealth();
  return (
    <section className="animate-rise">
      <PageHead eyebrow="DIAGNOSTICS" title="Alerts & health" />
      {health.isPending && <LoadingState rows={6} />}
      {health.isError && (
        <ErrorState {...failureFrom(health.error)} onRetry={() => void health.refetch()} />
      )}
      {health.data !== undefined && <Diagnostics rows={health.data} />}
    </section>
  );
}

function Diagnostics({
  rows,
}: {
  rows: NonNullable<ReturnType<typeof useSourceHealth>['data']>;
}): JSX.Element {
  const failing = rows.filter((row) => row.status === 'error');
  return (
    <>
      {failing.length > 0 && (
        <div className="mb-6 flex flex-col gap-2.5">
          {failing.map((row) => (
            <AlertCard key={row.instanceId} row={row} />
          ))}
        </div>
      )}
      <div className={`${card} overflow-x-auto`}>
        <div className="min-w-[780px]">
          <div className={`${HEALTH_COLUMNS} ${monoLabel} border-line border-b py-3`}>
            <span />
            <span>SOURCE</span>
            <span>LATENCY</span>
            <span>P95</span>
            <span>LAST SYNC</span>
            <span className="text-right">STATUS</span>
          </div>
          {rows.length === 0 ? (
            <EmptyState
              title="No sources connected yet"
              body="Add one under Sources and its health shows up here."
            />
          ) : (
            rows.map((row) => <HealthRow key={row.instanceId} row={row} />)
          )}
        </div>
      </div>
    </>
  );
}
